import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';

export default function Hotel() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [userNote, setUserNote] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    async function fetchHotel() {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/hotels/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('isAdmin');
          navigate('/');
          return;
        }
        if (!res.ok) {
          const txt = await res.text().catch(() => null);
          throw new Error(`Failed to fetch hotel: ${res.status}${txt ? ' - ' + txt.slice(0,200) : ''}`);
        }
        const data = await res.json();
        setHotel(data);

        // fetch current profile
        try {
          if (token) {
            const profileRes = await fetch('/api/profile', { headers: { Authorization: `Bearer ${token}` } });
            if (profileRes.ok) {
              const profile = await profileRes.json().catch(() => null);
              if (profile && profile.user) setCurrentUser(profile.user);
            }
          }
        } catch (e) {
          // ignore profile errors
        }

        // fetch per-user note if applicable
        if (data && data.user_id && data.race_id) {
          try {
            const noteRes = await fetch(`/api/races/${data.race_id}/notes/${data.user_id}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (noteRes.ok) {
              const note = await noteRes.json().catch(() => null);
              setUserNote(note || null);
              setNoteDraft(note ? (note.notes || '') : '');
            }
          } catch (e) {
            // ignore
          }
        }
      } catch (err) {
        setError(err.message || String(err));
      } finally {
        setLoading(false);
      }
    }
    fetchHotel();
  }, [id, navigate]);

  async function saveNote() {
    if (!hotel || !hotel.race_id || !currentUser) return;
    setSavingNote(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/races/${hotel.race_id}/notes/${currentUser.id}`, {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, token ? { Authorization: `Bearer ${token}` } : {}),
        body: JSON.stringify({ notes: noteDraft }),
      });
      if (res.status === 401 || res.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('isAdmin');
        navigate('/');
        return;
      }
      if (!res.ok) {
        const txt = await res.text().catch(() => null);
        throw new Error(`Failed to save note: ${res.status}${txt ? ' - ' + txt.slice(0,200) : ''}`);
      }
      const updated = await res.json().catch(() => null);
      setUserNote(updated || { notes: noteDraft });
    } catch (e) {
      // set a simple error message if desired
      setError(e.message || String(e));
    } finally {
      setSavingNote(false);
    }
  }

  if (loading) return <div className="p-6">Loading hotel...</div>;
  if (error) return <div className="p-6 text-red-600">Error: {error}</div>;
  if (!hotel) return <div className="p-6">Hotel not found</div>;

  // small helpers
  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', {day: 'numeric', month: 'long', year: 'numeric'}) : '-';
  const fmtTime = (d) => d ? new Date(d).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : '-';

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900">{hotel.hotel_name}</h1>
          <p className="text-sm text-gray-500 mt-1">{hotel.city || hotel.country || ''}</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/hotels" className="text-sm text-indigo-600 hover:underline">← Back to hotels</Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left: image + basic info */}
        <div className="md:col-span-2 bg-white rounded-lg shadow p-4">
          <div className="h-56 bg-gray-100 rounded-md overflow-hidden mb-4 flex items-center justify-center">
            {/* placeholder image area */}
            <img
              alt={hotel.hotel_name}
              src={hotel.image_url || 'https://placehold.co/800x400?text=Hotel+photo'}
              className="object-cover w-full h-full"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-blue-50 p-3 rounded-lg border-2 border-blue-200">
              <h3 className="text-xs font-semibold text-blue-700 uppercase">Check-In</h3>
              <p className="text-lg font-bold text-blue-900 mt-1">{fmtDate(hotel.check_in)}</p>
              <p className="text-sm text-blue-700">{fmtTime(hotel.check_in)}</p>
            </div>
            <div className="bg-green-50 p-3 rounded-lg border-2 border-green-200">
              <h3 className="text-xs font-semibold text-green-700 uppercase">Check-Out</h3>
              <p className="text-lg font-bold text-green-900 mt-1">{fmtDate(hotel.check_out)}</p>
              <p className="text-sm text-green-700">{fmtTime(hotel.check_out)}</p>
            </div>
          </div>

          <div className="mb-4">
            <h3 className="text-sm text-gray-500">Address</h3>
            <p className="font-medium">{hotel.address || '-'}</p>
          </div>

          <div className="mb-4">
            <h3 className="text-sm text-gray-500">Description</h3>
            <p className="mt-1 text-gray-700">{hotel.description || 'No description available.'}</p>
          </div>


        </div>

        {/* Right: admin notes + user note editor */}
        <aside className="space-y-4">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm text-gray-500">Admin notes</h3>
            <div className="mt-2 text-gray-700 whitespace-pre-wrap">{hotel.hotel_notes || <span className="text-gray-400">No admin notes</span>}</div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm text-gray-500">Your note for this race</h3>
            {currentUser ? (
              <div className="mt-2">
                <textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  rows={6}
                  className="w-full border rounded p-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  placeholder="Private note for your own reference..."
                />
                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs text-gray-500">Notes are private to you.</div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setNoteDraft(userNote ? (userNote.notes || '') : ''); }}
                      className="text-sm text-gray-600 hover:underline"
                      disabled={savingNote}
                    >Reset</button>
                    <button
                      onClick={saveNote}
                      className={`inline-flex items-center px-3 py-1.5 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700 ${savingNote ? 'opacity-60 cursor-not-allowed' : ''}`}
                      disabled={savingNote}
                    >{savingNote ? 'Saving…' : 'Save'}</button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-2 text-sm text-gray-600">Please <Link to="/" className="text-indigo-600 hover:underline">log in</Link> to edit your personal note.</div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm text-gray-500">Location</h3>
            <div className="mt-2">
              <div className="text-gray-700">{hotel.address || '-'}</div>
              {hotel.address && (
                <a
                  className="text-indigo-600 hover:underline text-sm"
                  target="_blank"
                  rel="noreferrer"
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(hotel.address)}`}
                >Open in Google Maps</a>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
